import { jsx } from "react/jsx-runtime";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useContext, useCallback, useState, useEffect, createContext } from "react";
import { deepmerge } from "@mui/utils";
import { THEME_PREFIX } from "./types.js";
import { produce } from "immer";
import { isWhite } from "../utils/stringUtils.js";
import brandclub_colors from "../colors/brandclubColors.js";
const processTheme = (themeOptions) => {
  if (!themeOptions) return themeOptions;
  const sanitizedThemeOptions = produce(themeOptions, (draft) => {
    var _a, _b, _c, _d, _e, _f, _g;
    draft ?? (draft = {});
    delete draft.customCss;
    const processThemeUrls = (obj) => {
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (typeof value === "string" && value.match(/https?:\/\//)) {
          obj[key] = `url(${value})`;
          return;
        }
        if (value && typeof value === "object" && !Array.isArray(value)) {
          processThemeUrls(value ?? {});
        }
      });
    };
    processThemeUrls(draft);
    if (draft.typography && typeof draft.typography === "object") {
      const typography = draft.typography;
      Object.keys(typography).forEach((key) => {
        const variant = typography[key];
        if (variant && typeof variant === "object" && variant.lineHeight === "inherit") {
          variant.lineHeight = "normal";
        }
      });
    }
    let backdropColor = brandclub_colors.black;
    if (((_b = (_a = themeOptions.palette) == null ? void 0 : _a.text) == null ? void 0 : _b.primary) && !isWhite((_d = (_c = themeOptions.palette) == null ? void 0 : _c.text) == null ? void 0 : _d.primary)) {
      backdropColor = themeOptions.palette.text.primary;
    }
    draft.components ?? (draft.components = {});
    (_e = draft.components).MuiBackdrop ?? (_e.MuiBackdrop = {});
    (_f = draft.components.MuiBackdrop).styleOverrides ?? (_f.styleOverrides = {});
    draft.components.MuiBackdrop.styleOverrides.root = {
      backgroundColor: `rgba(${(_g = backdropColor.replace("#", "").match(/.{1,2}/g)) == null ? void 0 : _g.map((hex) => parseInt(hex, 16)).join(", ")}, 0.21)`,
      transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      zIndex: 900
    };
  });
  return sanitizedThemeOptions;
};
const UpdateCustomThemeContext = createContext(null);
const CustomThemeOptionsContext = createContext(void 0);
const useUpdateCustomTheme = () => {
  const _updateCustomTheme = useContext(UpdateCustomThemeContext);
  const customThemeOptions = useContext(CustomThemeOptionsContext);
  if (!_updateCustomTheme || !customThemeOptions) {
    throw new Error("useUpdateCustomTheme must be used within a CustomThemeProvider");
  }
  const updateCustomTheme = useCallback(
    (update, forceUpdate = false) => {
      if (forceUpdate) {
        return _updateCustomTheme(update);
      }
      return _updateCustomTheme(
        (prev) => deepmerge(prev, typeof update === "function" ? update(prev) : update, { clone: true })
      );
    },
    [_updateCustomTheme, customThemeOptions]
  );
  return [customThemeOptions, updateCustomTheme];
};
const WithUpdateCustomThemeOptions = (props) => {
  const [themeOptions, setThemeOptions] = useState(props.themeOptions);
  useEffect(() => {
    setThemeOptions(props.themeOptions);
  }, [props.themeOptions]);
  return /* @__PURE__ */ jsx(UpdateCustomThemeContext.Provider, { value: setThemeOptions, children: /* @__PURE__ */ jsx(CustomThemeOptionsContext.Provider, { value: themeOptions, children: props.children }) });
};
const CustomThemeProviderConsumer = (props) => {
  const [themeOptions] = useUpdateCustomTheme();
  const theme = createTheme({
    ...processTheme(themeOptions),
    cssVariables: {
      cssVarPrefix: THEME_PREFIX
    }
  });
  return /* @__PURE__ */ jsx(ThemeProvider, { theme, children: props.children });
};
const CustomThemeProvider = (props) => {
  return /* @__PURE__ */ jsx(WithUpdateCustomThemeOptions, { themeOptions: props.themeOptions, children: /* @__PURE__ */ jsx(CustomThemeProviderConsumer, { children: props.children }) });
};
export {
  CustomThemeProvider,
  useUpdateCustomTheme
};
//# sourceMappingURL=customTheme.js.map
